import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {CustomerHierarchyContext} from "../PA.Utils/CustomerHierarchyContext";
import * as AsyncLock from 'async-lock';
import {IDateRange} from "../PA.Core/PA.Core.IF.Basic";
import {PeriodService} from "./PeriodService";
import {DateUtils} from "../PA.Core/Basic/Dates";
import * as _ from 'lodash';

export class CustomerHierarchyService {

    private static dctCustHierContextCache: { [id: string]: CustomerHierarchyContext } = {};
    private static dctLock: any = new AsyncLock();

    static getWbIDsByCustomerType(custHierWbID: number, customerType: string): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getWBIdsByCustomerType(customerType);
                })
                .then((keys) => {
                    resolve(keys);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getParent(custHierWbID: number, custWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, customerType?: string, depthLimit?: number): Promise<number> {
        return new Promise<number>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getParentWbID(custWbID, effectiveStartDt, effectiveEndDt, customerType, depthLimit);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getAllParents(custHierWbID: number, custWbID: number): Promise<{ wbID: number, dateRange: IDateRange }[]> {
        return new Promise<{ wbID: number, dateRange: IDateRange }[]>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getAllCustParents(custWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static isCustWbIdInHierarchy(custHierWbID: number, custWbID: number): Promise<boolean> {
        return new Promise<boolean>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.isCustWbIdInHierarchy(custWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getChildren(custHierWbID: number, custWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, customerType: string, depthLimit?: number): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getChildren(custWbID, effectiveStartDt, effectiveEndDt, customerType, depthLimit);
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getParentByPeriodWbID(custHierWbID: number, custWbID: number, periodWbID: number, customerType?: string, depthLimit?: number): Promise<number> {
        return new Promise<number>((resolve, error) => {

            let dates: IDateRange;
            CustomerHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange: IDateRange) => {

                    if (_.isUndefined(dateRange)) {
                        resolve(undefined);
                        return;
                    }

                    dates = dateRange;
                    return CustomerHierarchyService.getHierarchyContext(custHierWbID);
                })
                .then((context) => {
                    return context.getParentWbID(custWbID, dates.StartDt, dates.EndDt, customerType, depthLimit);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getChildrenByPeriodWbID(custHierWbID: number, custWbID: number, periodWbID: number, customerType?: string, depthLimit?: number): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            let dates: IDateRange;
            CustomerHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve([]);
                        return;
                    }

                    dates = dateRange;
                    return CustomerHierarchyService.getHierarchyContext(custHierWbID);
                })
                .then((context) => {
                    return context.getChildren(custWbID, dates.StartDt, dates.EndDt, customerType, depthLimit);
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    private static getHierarchyContext(custHierWbID: number): Promise<CustomerHierarchyContext> {
        return new Promise<CustomerHierarchyContext>((resolve, error) => {
            CustomerHierarchyService.dctLock.acquire('global',
                (done: any) => {
                    let context: CustomerHierarchyContext;

                    if (custHierWbID in CustomerHierarchyService.dctCustHierContextCache) {
                        context = CustomerHierarchyService.dctCustHierContextCache[custHierWbID];
                    } else {
                        context = new CustomerHierarchyContext(custHierWbID);
                        CustomerHierarchyService.dctCustHierContextCache[custHierWbID] = context;
                    }

                    done(null, CustomerHierarchyService.dctCustHierContextCache[custHierWbID]);
                    resolve(context);
                })
        })
    }

    static getLeaseClasses(custHierWbID: number): string[] {
        let retVal: string[] = [];
        retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Customer_Hierarchy, custHierWbID));
        return retVal;
    }

    static clearCacheByHierarchyId(custHierWbID: number): Promise<void> {
        return new Promise<void>((resolve, error) => {
            CustomerHierarchyService.getHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.reset();
                })
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    private static resetCache(): string {
        let retVal: string = "Cleared " + _.values(CustomerHierarchyService.dctCustHierContextCache).length + " customer hierarchies.";
        CustomerHierarchyService.dctCustHierContextCache = {};
        return retVal;
    }

    private static getDateRangeFromPeriodWbID(periodWbID: number): Promise<IDateRange> {
        return new Promise<IDateRange>((resolve, error) => {
            PeriodService.getPeriodByWBID(periodWbID)
                .then((result) => {
                    if (!_.isUndefined(result)) {
                        resolve({
                            StartDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_START_DT),
                            EndDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_END_DT)
                        });
                    } else {
                        resolve(undefined);
                    }
                })
                .catch((err) => {
                    error(err);
                })
        })
    }
}

